import { CollectionDocument } from '@/shared/types/collections';

import { DatasourceHealthKind, getDatasourceHealthKind, isDocumentRow } from '../helper';
import { DatasourceDocument } from '../types';

/**
 * Best first, matching the document order below where `available` sorts first. Every value stays
 * far above the document weights, so datasource rows still sort last as a block, and below the 999
 * a row with no verdict keeps, so the rows that have something to say come first.
 */
const HEALTH_ORDER: Record<DatasourceHealthKind, number> = {
  healthy: 991,
  indexing: 992,
  warning: 993,
  error: 994,
  failed: 995,
};

/**
 * Creates a sortable value for datasource documents based on status and draft state.
 *
 * This function implements a specific sorting priority:
 * 1. Available documents (published first, then draft)
 * 2. Indexing documents (published first, then draft)
 * 3. Pending documents (published first, then draft)
 * 4. Other status documents (published first, then draft)
 * 5. Datasource/folder rows (always last)
 *
 * Within each status group, published documents sort before draft documents.
 *
 * @param row - The datasource document row to generate a sort value for
 * @returns A numeric value used for sorting (lower values sort first)
 */
export const getStatusSortValue = (row: DatasourceDocument | CollectionDocument): number => {
  // Datasource rows (folders) have no document status, so they sort last, ordered by the crawl
  // verdict the Health column shows rather than tied at one value.
  if (!isDocumentRow(row)) {
    const health = getDatasourceHealthKind(row as DatasourceDocument);
    return health ? HEALTH_ORDER[health.kind] : 999;
  }

  // For document rows, sort by: Available → Indexing → Pending → others
  // Also consider draft state within each status group
  let statusWeight: number;
  switch (row.status) {
    case 'available':
      statusWeight = 1;
      break;
    case 'indexing':
      statusWeight = 2;
      break;
    case 'pending':
      statusWeight = 3;
      break;
    default:
      statusWeight = 4;
  }

  // Published items should sort before draft items of the same status
  const draftWeight = row.is_draft ? 0.5 : 0;
  return statusWeight + draftWeight;
};
